import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bookmark, BookmarkCheck, CheckCircle2, RotateCcw, ChevronUp, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useReads } from "@/contexts/ReadsContext";
import { BLOG } from "@/constants/testIds";
import { toast } from "sonner";
import CommentsSection from "@/components/CommentsSection";
import GlossaryEnhancer from "@/components/GlossaryEnhancer";
import { ReadBadge } from "@/components/ReadIndicators";
import { startBlogTracker } from "@/lib/tracker";
import ShareBar from "@/components/ShareBar";
import { loadBlogDisplaySettings } from "@/lib/blogDisplay";
import SectionRenderer from "@/components/SectionRenderer";

export default function BlogReader() {
  const { slug } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { isRead, markRead, markUnread } = useReads();
  const [blog, setBlog] = useState(null);
  const [author, setAuthor] = useState(null);
  const [pathway, setPathway] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [tocLabel, setTocLabel] = useState("Blog Contents");
  const [display, setDisplay] = useState({
    show_byline: true, show_added_date: true, show_share_bar: true, show_read_indicators: true,
    bibliography_url_render: true, bibliography_url_style: "subtle",
  });
  const [veSettings, setVeSettings] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    api.get(`/blogs/${slug}`).then(async r => {
      setBlog(r.data);
      if (r.data.author_id) {
        api.get("/authors").then(ar => {
          setAuthor(ar.data.find(a => a.author_id === r.data.author_id) || null);
        });
      }
      // Find a pathway that contains this blog. If multiple, use the first.
      // Then fetch that pathway's blogs (in order) for the "In this pathway" sidebar.
      try {
        const paths = await api.get("/pathways");
        const containing = paths.data.find(p => (p.blog_ids || []).includes(r.data.blog_id));
        if (containing) {
          setPathway(containing);
          const pathwayDetail = await api.get(`/pathways/${containing.slug}`);
          setSiblings(pathwayDetail.data.blogs || []);
        } else {
          setPathway(null);
          setSiblings([]);
        }
      } catch { setPathway(null); setSiblings([]); }
    });
  }, [slug]);

  useEffect(() => {
    if (!user || !blog) return;
    api.get("/bookmarks").then(r => {
      const isB = r.data.raw.some(x => x.target_type === "blog" && x.target_id === blog.blog_id);
      setBookmarked(isB);
    }).catch(() => {});
  }, [user, blog]);

  // Auto-mark this blog as read once loaded (login required).
  // Uses a ref so the effect doesn't re-fire when markRead's identity changes
  // (which would otherwise immediately re-mark a blog the user just marked unread).
  const markReadRef = useRef(markRead);
  useEffect(() => { markReadRef.current = markRead; }, [markRead]);
  useEffect(() => {
    if (user && blog?.blog_id) markReadRef.current(blog.blog_id);
  }, [user, blog?.blog_id]);

  // Analytics tracking: fire view event + heartbeat engagement while user reads
  useEffect(() => {
    if (!blog?.blog_id) return;
    const stop = startBlogTracker(blog, contentRef);
    return stop;
  }, [blog?.blog_id]);

  // Load the admin-configured Blog Display settings (TOC label, visibility toggles, etc.)
  useEffect(() => {
    loadBlogDisplaySettings().then(s => {
      if (s?.toc_label) setTocLabel(s.toc_label);
      setDisplay({
        show_byline: s?.show_byline !== false,
        show_added_date: s?.show_added_date !== false,
        show_share_bar: s?.show_share_bar !== false,
        show_read_indicators: s?.show_read_indicators !== false,
        bibliography_url_render: s?.bibliography_url_render !== false,
        bibliography_url_style: s?.bibliography_url_style || "subtle",
      });
    });
    api.get("/site-settings/visual-editor").then(r => setVeSettings(r.data)).catch(() => setVeSettings({}));
  }, []);

  // Push SEO description → document.head meta tag while this blog is mounted.
  useEffect(() => {
    if (!blog) return;
    const desc = (blog.seo_description || blog.excerpt || "").trim();
    let tag = document.querySelector('meta[name="description"]');
    if (!tag && desc) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    const prevDesc = tag?.getAttribute("content");
    if (desc && tag) tag.setAttribute("content", desc);
    const prevTitle = document.title;
    if (blog.title) document.title = blog.title;
    return () => {
      if (tag && prevDesc != null) tag.setAttribute("content", prevDesc);
      document.title = prevTitle;
    };
  }, [blog?.seo_description, blog?.excerpt, blog?.title]);

  // If we arrived from a search result with ?q=, highlight EVERY match in the
  // blog HTML *string* — including which one is currently "active" — so nothing
  // React re-renders in the middle of things (bookmarks fetch, tracker, etc.)
  // can strip our class. useMemo re-runs when activeMatch changes to move the
  // is-active/pulse decoration to the newly-focused match.
  const searchQuery = new URLSearchParams(location.search).get("q");
  const [showNav, setShowNav] = useState(true);
  const [activeMatch, setActiveMatch] = useState(0);
  const { sections: highlightedSections, matchCount } = useMemo(() => {
    if (!blog?.sections) return { sections: [], matchCount: 0 };
    const q = (searchQuery || "").trim();
    if (!q || !showNav) return { sections: (blog?.sections || []).filter(s => !s.hidden), matchCount: 0 };
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const fuzzy = escaped.replace(/[\s\-_]+/g, "[\\s\\-_]+");
    let rx;
    try { rx = new RegExp(fuzzy, "gi"); } catch { return { sections: blog.sections.filter(s => !s.hidden), matchCount: 0 }; }
    let idxCounter = 0;
    const out = blog.sections.filter(s => !s.hidden).map((s) => {
      const html = s.html || "";
      const tagOrText = /<[^>]+>|([^<]+)/g;
      let result = "";
      let m;
      let changed = false;
      while ((m = tagOrText.exec(html)) !== null) {
        if (!m[1]) { result += m[0]; continue; }
        const inner = m[1];
        rx.lastIndex = 0;
        let piece = "";
        let last = 0;
        let hit;
        while ((hit = rx.exec(inner)) !== null) {
          if (hit[0].length === 0) { rx.lastIndex++; continue; }
          const isActive = idxCounter === activeMatch;
          const classes = "search-hit" + (isActive ? " is-active pulse" : "");
          piece += inner.slice(last, hit.index)
                +  `<mark class="${classes}" data-match-index="${idxCounter}">${hit[0]}</mark>`;
          last = hit.index + hit[0].length;
          idxCounter++;
          changed = true;
        }
        piece += inner.slice(last);
        result += piece;
      }
      return changed ? { ...s, html: result } : s;
    });
    return { sections: out, matchCount: idxCounter };
  }, [blog, searchQuery, showNav, activeMatch]);

  // Clamp activeMatch when match count changes
  useEffect(() => {
    if (activeMatch >= matchCount) setActiveMatch(0);
  }, [matchCount, activeMatch]);

  // Smooth-scroll the active mark into view. The is-active + pulse classes are
  // already baked into the HTML by the useMemo above, so we don't touch the DOM
  // ourselves — we just scroll. CSS animation-delay handles playing the pulse
  // *after* the scroll settles so it doesn't fire while off-screen.
  useEffect(() => {
    if (!searchQuery || matchCount === 0 || !contentRef.current || !showNav) return;
    const timer = setTimeout(() => {
      const target = contentRef.current?.querySelector(`mark.search-hit[data-match-index="${activeMatch}"]`);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    return () => clearTimeout(timer);
  }, [activeMatch, matchCount, searchQuery, highlightedSections, showNav]);

  const gotoMatch = (delta) => {
    if (matchCount === 0) return;
    setActiveMatch((i) => (i + delta + matchCount) % matchCount);
  };
  // Keyboard shortcuts: n = next match, p = prev match. Only when nav is visible.
  useEffect(() => {
    if (!searchQuery || matchCount === 0 || !showNav) return;
    const onKey = (e) => {
      if (e.target.matches?.("input, textarea, [contenteditable]")) return;
      if (e.key === "n" || e.key === "ArrowDown") { e.preventDefault(); gotoMatch(1); }
      else if (e.key === "p" || e.key === "ArrowUp") { e.preventDefault(); gotoMatch(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchQuery, matchCount, showNav]);

  useEffect(() => {
    if (!blog) return;
    const handler = () => {
      const headings = blog.sections.filter(s => !s.hidden && s.level > 0 && (s.heading || "").trim()).map(s => document.getElementById(s.id)).filter(Boolean);
      let current = headings[0]?.id;
      for (const h of headings) {
        const top = h.getBoundingClientRect().top;
        if (top < 140) current = h.id; else break;
      }
      setActiveId(current || null);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [blog]);

  const toggleBookmark = async () => {
    if (!user) return toast("Sign in to bookmark");
    const r = await api.post("/bookmarks/toggle", { target_type: "blog", target_id: blog.blog_id });
    setBookmarked(r.data.bookmarked);
    toast(r.data.bookmarked ? "Bookmarked" : "Removed bookmark");
  };

  const currentIndex = useMemo(() => {
    if (!blog || !siblings.length) return -1;
    return siblings.findIndex(b => b.blog_id === blog.blog_id);
  }, [blog, siblings]);

  if (!blog) return <div className="max-w-3xl mx-auto p-12 text-slate-500">Loading…</div>;

  return (
    <div data-testid={BLOG.reader} className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      {/* Floating "match navigator" — only when arriving from a search result */}
      {searchQuery && showNav && (
        <div data-testid="blog-match-nav"
             className="fixed bottom-6 right-6 z-40 flex items-center gap-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-lg pl-4 pr-1 py-1">
          <span className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mr-2">
            <span className="text-slate-700 dark:text-slate-200 font-semibold">
              {matchCount === 0 ? 0 : activeMatch + 1}
            </span>
            <span> / {matchCount}</span>
            <span className="hidden sm:inline"> matches for <span className="text-sky-700 dark:text-sky-300 font-medium normal-case">&ldquo;{searchQuery}&rdquo;</span></span>
          </span>
          <button data-testid="blog-match-prev"
                  onClick={() => gotoMatch(-1)}
                  disabled={matchCount === 0}
                  title="Previous match (P or ↑)"
                  className="w-8 h-8 grid place-items-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-colors">
            <ChevronUp className="w-4 h-4"/>
          </button>
          <button data-testid="blog-match-next"
                  onClick={() => gotoMatch(1)}
                  disabled={matchCount === 0}
                  title="Next match (N or ↓)"
                  className="w-8 h-8 grid place-items-center rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30 disabled:pointer-events-none transition-colors">
            <ChevronDown className="w-4 h-4"/>
          </button>
          <button data-testid="blog-match-close"
                  onClick={() => setShowNav(false)}
                  title="Dismiss"
                  className="w-8 h-8 grid place-items-center rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X className="w-4 h-4"/>
          </button>
        </div>
      )}
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Desktop TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div data-testid={BLOG.tocDesktop} className="sticky top-24">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{tocLabel}</div>
            <nav className="space-y-1 border-l border-slate-200">
              {blog.sections.filter(s => !s.hidden && s.level > 0 && (s.heading || "").trim()).map(s => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={BLOG.tocLink(s.id)}
                  className={`block pl-4 py-1.5 text-sm transition-colors ${
                    activeId === s.id
                      ? "border-l-2 -ml-px border-sky-700 text-sky-800 font-semibold"
                      : "text-slate-500 hover:text-slate-900"
                  } ${s.level === 1 ? "toc-h1 text-[15px]" : s.level === 3 ? "toc-h3 text-[13px]" : "toc-h2"}`}
                >
                  {s.heading}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 max-w-3xl">
          <header className="mb-8 pb-8 border-b border-slate-200">
            {pathway && (
              <Link to={`/pathways/${pathway.slug}`} className="text-xs font-bold uppercase tracking-[0.24em] text-sky-700 hover:text-sky-900">
                {pathway.title}
              </Link>
            )}
            <h1 data-testid={BLOG.title} className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight mt-3 leading-[1.1]">
              {blog.title}
            </h1>
            {blog.subtitle && (
              <p data-testid="blog-subtitle" className="mt-3 font-heading text-xl md:text-2xl text-slate-600 font-normal italic leading-snug">
                {blog.subtitle}
              </p>
            )}
            {(blog.author_name || blog.first_published || blog.last_updated) && (
              <div data-testid="blog-byline-doc" className="mt-4 text-sm text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
                {blog.author_name && (
                  <span>By <span className="text-slate-700 font-medium">{blog.author_name}</span></span>
                )}
                {blog.first_published && (
                  <span>Published {new Date(blog.first_published).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
                )}
                {blog.last_updated && blog.last_updated !== blog.first_published && (
                  <span>· Updated {new Date(blog.last_updated).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>
                )}
              </div>
            )}
            {user?.role === "admin" && (blog.primary_module || (blog.secondary_modules || []).length > 0) && (
              <div data-testid="blog-module-chips" className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-medium mr-1">
                  Admin only:
                </span>
                {blog.primary_module && (
                  <span className="text-[10px] uppercase tracking-widest bg-sky-100 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 px-2 py-0.5 rounded font-mono">
                    {blog.primary_module}
                  </span>
                )}
                {(blog.secondary_modules || []).map((m, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded font-mono">
                    {m}
                  </span>
                ))}
              </div>
            )}
            {blog.header_image_url && (
              <figure data-testid="blog-header-image" className="mt-6 rounded-md overflow-hidden border border-slate-200 dark:border-slate-800">
                <img src={blog.header_image_url} alt={blog.header_image_alt || blog.title}
                     className="w-full h-auto block"/>
              </figure>
            )}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Button
                data-testid={BLOG.bookmark}
                onClick={toggleBookmark}
                variant="outline"
                className="border-slate-300"
              >
                {bookmarked
                  ? <><BookmarkCheck className="w-4 h-4 mr-2 text-sky-700"/>Bookmarked</>
                  : <><Bookmark className="w-4 h-4 mr-2"/>Bookmark</>}
              </Button>
              <span className="text-xs text-slate-500">
                {display.show_added_date && <>Added {new Date(blog.created_at).toLocaleDateString()}</>}
              </span>
              {user && isRead(blog.blog_id) && display.show_read_indicators && (
                <>
                  <ReadBadge read className="ml-1"/>
                  <button
                    data-testid="blog-mark-unread-btn"
                    onClick={async () => { await markUnread(blog.blog_id); toast("Marked as unread"); }}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 hover:text-sky-700"
                    title="Mark as unread"
                  >
                    <RotateCcw className="w-3.5 h-3.5"/> Mark unread
                  </button>
                </>
              )}
              {display.show_share_bar && (
                <ShareBar title={blog.title} url={typeof window !== "undefined" ? window.location.href : ""}
                          className="ml-auto"/>
              )}
            </div>
          </header>

          <div className="blog-content" ref={contentRef}>
            {highlightedSections.map(s => (
              <section key={s.id} id={s.id}>
                <SectionRenderer section={s} veSettings={veSettings} blogDisplay={display}/>
              </section>
            ))}
            {highlightedSections.length === 0 && (
              <p className="text-slate-500 italic">This blog has no content sections yet.</p>
            )}
          </div>
          <GlossaryEnhancer containerRef={contentRef} />

          {/* Legacy top-level summary_321 + bibliography — only render for OLD blogs
              that still carry these fields. New blogs surface them as inline
              editorial sections via the SectionRenderer. */}
          {(!blog.sections?.some(s => s.subtype === "summary_321")) && blog.summary_321 && (
            (blog.summary_321.insights?.length > 0 || blog.summary_321.actions?.length > 0 || blog.summary_321.question) && (
              <section data-testid="blog-summary-321"
                       className="mt-14 pt-8 border-t border-slate-200 dark:border-slate-800">
                <div className="text-xs font-bold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-400 mb-4">3-2-1 Summary</div>
                <div className="grid md:grid-cols-3 gap-4">
                  {blog.summary_321.insights?.length > 0 && (
                    <div className="rounded-md border border-sky-100 bg-sky-50 dark:bg-sky-950/20 dark:border-sky-900 p-5">
                      <div className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">3 Insights</div>
                      <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300 list-decimal pl-5 marker:text-sky-700 marker:font-semibold">
                        {blog.summary_321.insights.map((it, i) => (
                          <li key={i} className="leading-relaxed">{it}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {blog.summary_321.actions?.length > 0 && (
                    <div className="rounded-md border border-emerald-100 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900 p-5">
                      <div className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">2 Actions</div>
                      <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300 list-decimal pl-5 marker:text-emerald-700 marker:font-semibold">
                        {blog.summary_321.actions.map((it, i) => (
                          <li key={i} className="leading-relaxed">{it}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {blog.summary_321.question && (
                    <div className="rounded-md border border-amber-100 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-5 flex flex-col justify-center">
                      <div className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">1 Question</div>
                      <p className="text-lg italic text-slate-800 dark:text-slate-200 leading-snug">
                        &ldquo;{blog.summary_321.question}&rdquo;
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )
          )}

          {(!blog.sections?.some(s => s.subtype === "bibliography")) && blog.bibliography?.length > 0 && (
            <section data-testid="blog-bibliography"
                     className="mt-14 pt-8 border-t border-slate-200 dark:border-slate-800">
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-4">Bibliography</div>
              <ol className="space-y-3 list-decimal pl-6 text-sm text-slate-700 dark:text-slate-300">
                {blog.bibliography.map((ref, i) => (
                  <li key={i} className="leading-relaxed pl-1">{ref}</li>
                ))}
              </ol>
            </section>
          )}

          {author && display.show_byline && (
            <div className="mt-16 pt-8 border-t border-slate-200">
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-3">Author</div>
              {author.slug ? (
                <Link
                  data-testid={`byline-author-${author.author_id}`}
                  to={`/authors/${author.slug}`}
                  className="font-heading text-xl font-semibold text-slate-900 hover:text-sky-700 transition-colors"
                >
                  {author.name}
                </Link>
              ) : (
                <div className="font-heading text-xl font-semibold text-slate-900">{author.name}</div>
              )}
              {author.title && <div className="text-sm text-slate-500 mt-0.5">{author.title}</div>}
              {author.bio && <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">{author.bio}</p>}
            </div>
          )}

          <CommentsSection blogId={blog.blog_id} />

          {/* Pathway navigation — clean Prev / Next cards, no header strip */}
          {pathway && siblings.length > 0 && currentIndex >= 0 && (
            <nav data-testid="pathway-nav" className="mt-14 pt-8 border-t border-slate-200">
              <div className="grid md:grid-cols-2 gap-4">
                {currentIndex > 0 ? (
                  <Link data-testid="pathway-nav-prev" to={`/blogs/${siblings[currentIndex-1].slug}`}
                        className="group block border border-slate-200 rounded-lg p-4 hover:border-sky-700 transition-colors">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">← Previous</div>
                    <div className="font-heading font-semibold text-slate-900 group-hover:text-sky-700 line-clamp-2">
                      {siblings[currentIndex-1].title}
                    </div>
                  </Link>
                ) : <div/>}
                {currentIndex < siblings.length - 1 ? (
                  <Link data-testid="pathway-nav-next" to={`/blogs/${siblings[currentIndex+1].slug}`}
                        className="group block border border-slate-200 rounded-lg p-4 hover:border-sky-700 transition-colors md:text-right">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">Next →</div>
                    <div className="font-heading font-semibold text-slate-900 group-hover:text-sky-700 line-clamp-2">
                      {siblings[currentIndex+1].title}
                    </div>
                  </Link>
                ) : (
                  <Link data-testid="pathway-nav-complete" to={`/pathways/${pathway.slug}`}
                        className="group block border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900 rounded-lg p-4 hover:border-emerald-500 transition-colors md:text-right">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 mb-1">Pathway complete</div>
                    <div className="font-heading font-semibold text-emerald-800 dark:text-emerald-300 group-hover:text-emerald-900">
                      You&apos;ve reached the end — back to the pathway →
                    </div>
                  </Link>
                )}
              </div>
            </nav>
          )}

          {/* Mobile TOC (below content) */}
          <div className="lg:hidden mt-12 pt-8 border-t border-slate-200">
            <div data-testid={BLOG.tocMobile}>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{tocLabel}</div>
              <ol className="space-y-1 text-sm">
                {blog.sections.filter(s => !s.hidden && s.level > 0 && (s.heading || "").trim()).map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className={`block py-1.5 ${s.level === 1 ? "toc-h1 text-slate-900" : s.level === 3 ? "toc-h3 text-[13px] text-slate-500" : "toc-h2 text-slate-700"}`}>
                      <span className="text-slate-400 mr-2">{String(i+1).padStart(2,"0")}</span>{s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
